import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUser } from "@src/hooks/useUser";
import { UserRoleEnum } from "../../../types";
import { useRolesPermissions } from "@src/hooks/useRolesPermission";
import styles from "./AdminDashboardContent.module.scss";
import DashboardCard from "../cards/DashboardCard";
import ImageLessonRoadmaps from "../assets/card-icons/lesson-roadmaps.svg?url";
import ImageEducatorOnboarding from "../assets/card-icons/educator-onboarding.svg?url";
import ImageQuickstartArticles from "../assets/card-icons/quickstart-articles.svg?url";
import ImageUserManagement from "../assets/card-icons/user-management.svg?url";
import ImageUsageInsights from "../assets/card-icons/usage-insights.svg?url";
import ImageScheduleSupportCall from "../assets/card-icons/schedule-support-call.svg?url";
import ImageProfessionalDevelopment from "../assets/card-icons/professional-development.svg?url";
import ImageCaregiverResources from "../assets/card-icons/caregiver-resources.svg?url";
import ImageSchoolLeaderOnboarding from "../assets/card-icons/school-leader-onboarding.svg?url";
import ImageDistrictLeaderOnboarding from "../assets/card-icons/district-leader-onboarding.svg?url";
import ImageScheduleSupportCallDistrictAdmin from "../assets/card-icons/schedule-support-call-district-admin.svg?url";
import ImageBookCrowsResiliencePresentation from "../assets/card-icons/book-crows-resilience-presentation.svg?url";
import ImageWellbeingFoundations from "../assets/card-icons/wellbeing-foundations.svg?url";
import ImagePrograms from "../assets/card-icons/programs.svg?url";
import ImageStandaloneLessons from "../assets/card-icons/standalone-lessons.svg?url";
import ImageBreinPose from "../assets/card-icons/brein-pose-2.png?url";

type AdminDashboardContentProps = {
  isAussie: boolean | "" | undefined;
  openUserManage: () => void;
  openUsagePage: () => void;
  openProfessionalDevelopment: () => void;
  openCaregiver: () => void;
};

type CardItemProps = {
  title: string;
  description: string;
  icon?: string;
  icon2?: string;
  onClick?: () => void;
  backgroundColor?: "Orange" | "Blue" | "Green" | "Purple" | "Yellow";
}[];

function AdminDashboardContent({
  isAussie,
  openUserManage,
  openUsagePage,
  openProfessionalDevelopment,
  openCaregiver,
}: AdminDashboardContentProps) {
  const [user] = useUser();
  const history = useHistory();

  const isSouthAU = useMemo(
    () => (user?.customer?.state === "South Australia" ? true : false),
    [user?.customer]
  );

  const isAdmin = useRolesPermissions([UserRoleEnum.Admin]);
  const isDistrictAdmin = useRolesPermissions([UserRoleEnum.DistrictAdmin]);
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);

  const openEducatorOnboardingCard = useCallback(() => {
    if (isAussie) {
      history.push("/program/educatoronboarding?pid=5aDstSYmlT4S72kqh5h1O");
      return;
    } else {
      history.push("/program/educatoronboarding?pid=aLYHriE6Nx6geYDZ_gTij");
      return;
    }
  }, [isAussie, history]);

  const openLessonRoadmapsCard = useCallback(() => {
    if (isAussie) {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank"
      );
      return;
    } else {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank"
      );
      return;
    }
  }, [isAussie]);

  const openSchoolLeaderOnboarding = useCallback(() => {
    if (isAussie) {
      history.push("/standalone/school-leader-onboarding-aus");
      return;
    } else {
      history.push("/standalone/school-leader-onboarding");
      return;
    }
  }, [isAussie, history]);

  const openEducatorOnboardingUnit = useCallback(() => {
    window.open(
      "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
      "_blank"
    );
  }, []);

  const openScheduleSupportCall = useCallback(() => {
    window.open(
      "https://calendly.com/madison-hilliard/30min?month=2025-07",
      "_blank"
    );
  }, []);

  const openScheduleKickOffCall = useCallback(() => {
    window.open(
      "https://calendly.com/openparachute-rikki/kickoff-call",
      "_blank"
    );
  }, []);

  const openBookResilience = useCallback(() => {
    window.open(
      "https://calendly.com/adelaidecrows-foundation/open-parachute-wellbeing-visit?month=2025-08",
      "_blank"
    );
  }, []);

  const openWellbeingFoundationsCard = () => {
    history.replace(`/wellbeingfoundations?tab=student-introduction`)
  }

  const openPrograms = useCallback(() => {
    history.push("/lessons?tab=programs");
  }, [history]);

  const openStandaloneLessonsCard = useCallback(() => {
    history.replace("/lessons?tab=library");
  }, [history]);

  const openLessonLibraryCard = useCallback(() => {
    history.replace("/lessons?tab=library");
  }, [history]);

  const itemsAdminTools: CardItemProps = useMemo(() => {
    switch (isDistrictAdmin || isSuperAdmin) {
      case true:
        return [
          {
            title: "User\n Management",
            description:
              "Invite and manage staff access, permissions and usage",
            icon: ImageUserManagement,
            onClick: openUserManage,
            backgroundColor: "Purple",
          },
          {
            title: "Usage\n Insights",
            description:
              "A suite of resources to monitor staff usage across various areas of the program",
            icon: ImageUsageInsights,
            onClick: openUsagePage,
            backgroundColor: "Purple",
          },
          {
            title: "Schedule\n Support Call",
            description:
              "Book in a call with a representative to assist with implementing the program",
            icon: ImageScheduleSupportCallDistrictAdmin,
            onClick: openScheduleSupportCall,
            backgroundColor: "Purple",
          },
        ];

      default:
        if (isAussie) {
          return [
            {
              title: "User\n Management",
              description:
                "Invite and manage staff access, permissions and usage",
              icon: ImageUserManagement,
              onClick: openUserManage,
              backgroundColor: "Purple",
            },
            {
              title: "Usage\n Insights",
              description:
                "A suite of resources to monitor staff usage across various areas of the program",
              icon: ImageUsageInsights,
              onClick: openUsagePage,
              backgroundColor: "Purple",
            },
            {
              title: "Schedule\n Kick-Off Call",
              description:
                "Book in an introductory kick-off call with a representative to assist with implementing the program",
              icon: ImageScheduleSupportCall,
              onClick: openScheduleKickOffCall,
              backgroundColor: "Purple",
            },
          ];
        } else {
          return [
            {
              title: "User\n Management",
              description:
                "Invite and manage staff access, permissions and usage",
              icon: ImageUserManagement,
              onClick: openUserManage,
              backgroundColor: "Purple",
            },
            {
              title: "Usage\n Insights",
              description:
                "A suite of resources to monitor staff usage across various areas of the program",
              icon: ImageUsageInsights,
              onClick: openUsagePage,
              backgroundColor: "Purple",
            },
            {
              title: "Schedule\n Support Call",
              description:
                "Book in a call with a representative to assist with implementing the program",
              icon: ImageScheduleSupportCall,
              onClick: openScheduleSupportCall,
              backgroundColor: "Purple",
            },
          ];
        }
    }
  }, [isDistrictAdmin, isSuperAdmin]);

  const itemsSchoolOnboardingMaterials: CardItemProps = useMemo(() => {
    switch (isDistrictAdmin || isSuperAdmin) {
      case true:
        return [
          {
            title: "Educator\n Onboarding",
            description:
              "Prepare educators to confidently deliver the program in classrooms",
            icon: ImageEducatorOnboarding,
            onClick: openEducatorOnboardingCard,
            backgroundColor: "Blue",
          },
          {
            title: "School Leader\n Onboarding",
            description:
              "Access a plan for implementing Open Parachute in your school",
            icon: ImageSchoolLeaderOnboarding,
            onClick: openSchoolLeaderOnboarding,
            backgroundColor: "Blue",
          },
          {
            title: "District Leader\n Onboarding",
            description:
              "Access resources and materials for implementing Open Parachute across your district",
            icon: ImageDistrictLeaderOnboarding,
            onClick: openLessonRoadmapsCard,
            backgroundColor: "Blue",
          }
        ];
      default:
        if (isAussie) {
          return [
            {
              title: "Educator\n Onboarding",
              description:
                "Prepare educators to confidently deliver the program in classrooms",
              icon: ImageEducatorOnboarding,
              onClick: openEducatorOnboardingCard,
              backgroundColor: "Blue",
            },
            {
              title: "School Leader\n Onboarding",
              description:
                "Access a plan for implementing Open Parachute in your school",
              icon: ImageSchoolLeaderOnboarding,
              onClick: openSchoolLeaderOnboarding,
              backgroundColor: "Blue",
            },
            {
              title: "Lesson\n Roadmaps",
              description:
                "Suggested lesson planning for each Year level to guide you through the program",
              icon: ImageLessonRoadmaps,
              onClick: openLessonRoadmapsCard,
              backgroundColor: "Blue",
            }
          ];
        } else {
          return [
            {
              title: "Educator\n Onboarding",
              description:
                "Prepare educators to confidently deliver the program in classrooms",
              icon: ImageEducatorOnboarding,
              onClick: openEducatorOnboardingCard,
              backgroundColor: "Blue",
            },
            {
              title: "School Leader\n Onboarding",
              description:
                "Access a plan for implementing Open Parachute in your school",
              icon: ImageSchoolLeaderOnboarding,
              onClick: openSchoolLeaderOnboarding,
              backgroundColor: "Blue",
            },
            {
              title: "Lesson\n Roadmaps",
              description:
                "Lesson recommendations for every grade level to get started quickly",
              icon: ImageLessonRoadmaps,
              onClick: openLessonRoadmapsCard,
              backgroundColor: "Blue",
            }
          ];
        }
    }
  }, [isDistrictAdmin, isSuperAdmin]);

  const itemsBookResilience: CardItemProps = useMemo(() => {
    return [
      {
        title: "Book Crows\n Resilience \n Presentation",
        description:
          "Access a plan for implementing Open Parachute in your school",
        icon: ImageBookCrowsResiliencePresentation,
        icon2: ImageBreinPose,
        onClick: openBookResilience,
        backgroundColor: "Yellow",
      }
    ];
  }, []);

  const itemsClassroomLessons: CardItemProps = useMemo(() => {
    if (isAussie) {
      return [
        {
          title: "Wellbeing\n Foundations",
          description: "Student introduction lessons, brain break activities and skill-building exercises",
          icon: ImageWellbeingFoundations,
          onClick: openWellbeingFoundationsCard,
          backgroundColor: "Orange",
        },
        {
          title: "Programs",
          description: "Collections of lessons to allow deeper learning into a specific topic or area",
          icon: ImagePrograms,
          onClick: openPrograms,
          backgroundColor: "Orange",
        },
        {
          title: "Standalone\n Lessons",
          description: "View the main hub for all available standalone lessons in Years 7 through 12",
          icon: ImageStandaloneLessons,
          onClick: openStandaloneLessonsCard,
          backgroundColor: "Orange",
        }
      ]
    } else {
      return [
        {
          title: "Well-being\n Foundations",
          description: "Student introduction lessons, brain break activities and skill-building exercises",
          icon: ImageWellbeingFoundations,
          onClick: openWellbeingFoundationsCard,
          backgroundColor: "Orange",
        },
        {
          title: "Programs",
          description: "Collections of lessons to allow deeper learning into a specific topic or area",
          icon: ImagePrograms,
          onClick: openPrograms,
          backgroundColor: "Orange",
        },
        {
          title: "Lesson\n Library",
          description: "View the main hub for our full catalog of standalone lessons",
          icon: ImageStandaloneLessons,
          onClick: openLessonLibraryCard,
          backgroundColor: "Orange",
        }
      ]
    }
  }, [isAussie]);

  const itemsSupportForEducatorsAndCaregivers: CardItemProps = useMemo(() => {
    switch (user?.role) {
      case UserRoleEnum.SuperAdmin || UserRoleEnum.DistrictAdmin:
        return [
          {
            title: "Quickstart\n Articles",
            description:
              "Equip your staff with informative Help Center guides for using the platform and running lessons",
            icon: ImageQuickstartArticles,
            onClick: openEducatorOnboardingUnit,
            backgroundColor: "Green",
          },
          {
            title: "Professional\n Development",
            description:
              "A suite of professional development resources for educators",
            icon: ImageProfessionalDevelopment,
            onClick: openProfessionalDevelopment,
            backgroundColor: "Green",
          },
          {
            title: "Caregiver\n Resources",
            description:
              "Resources for parents & caregivers to support their child at home",
            icon: ImageCaregiverResources,
            onClick: openCaregiver,
            backgroundColor: "Green",
          }
        ];
      default:
        if (isAussie) {
          return [
            {
              title: "Quickstart\n Articles",
              description:
                "Equip your staff with informative Help Center guides for using the platform and running lessons",
              icon: ImageQuickstartArticles,
              onClick: openEducatorOnboardingUnit,
              backgroundColor: "Green",
            },
            {
              title: "Professional\n Development",
              description:
                "A suite of professional development resources for educators",
              icon: ImageProfessionalDevelopment,
              onClick: openProfessionalDevelopment,
              backgroundColor: "Green",
            },
            {
              title: "Caregiver\n Resources",
              description:
                "Resources for parents & caregivers to support their child at home",
              icon: ImageCaregiverResources,
              onClick: openCaregiver,
              backgroundColor: "Green",
            }
          ];
        } else {
          return [
            {
              title: "Quickstart\n Articles",
              description:
                "Equip your staff with informative Help Center guides for using the platform and running lessons",
              icon: ImageQuickstartArticles,
              onClick: openEducatorOnboardingUnit,
              backgroundColor: "Green",
            },
            {
              title: "Professional\n Development",
              description:
                "A suite of professional development resources for educators",
              icon: ImageProfessionalDevelopment,
              onClick: openProfessionalDevelopment,
              backgroundColor: "Green",
            },
            {
              title: "Caregiver\n Resources",
              description:
                "Resources for parents & caregivers to support their child at home",
              icon: ImageCaregiverResources,
              onClick: openCaregiver,
              backgroundColor: "Green",
            }
          ];
        }
    }
  }, [user?.role]);

  return (
    <div className={styles.mainView}>
      <div className={styles.panelContainer}>
        <h2 className={styles.panelTitle}>Admin Tools</h2>
        <div className={styles.cardGroup}>
          {itemsAdminTools.map((item, index) => (
            <DashboardCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              onClick={item.onClick}
              backgroundColor={item.backgroundColor}
            />
          ))}
        </div>
      </div>

      <div className={styles.panelContainer}>
        <h2 className={styles.panelTitle}>
          {isDistrictAdmin || isSuperAdmin ? "District Onboarding Materials" : "School Onboarding Materials"}
        </h2>
        <div className={styles.cardGroup}>
          {itemsSchoolOnboardingMaterials.map((item, index) => (
            <DashboardCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              icon2={item.icon2}
              onClick={item.onClick}
              backgroundColor={item.backgroundColor}
            />
          ))}
        </div>
        {isSouthAU && (
          <div className={styles.cardGroup}>
            {itemsBookResilience.map((item, index) => (
              <DashboardCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                icon2={item.icon2}
                onClick={item.onClick}
                backgroundColor={item.backgroundColor}
              />
            ))}
          </div>
        )}
      </div>

      {
        isAdmin ? (
          <div className={styles.panelContainer}>
            <h2 className={styles.panelTitle}>
              Classroom Lessons
            </h2>
            <div className={styles.cardGroup}>
              {itemsClassroomLessons.map((item, index) => (
                <DashboardCard
                  key={index}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  onClick={item.onClick}
                  backgroundColor={item.backgroundColor}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.panelContainer}>
            <h2 className={styles.panelTitle}>
              Support for Educators & Caregivers
            </h2>
            <div className={styles.cardGroup}>
              {itemsSupportForEducatorsAndCaregivers.map((item, index) => (
                <DashboardCard
                  key={index}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  onClick={item.onClick}
                  backgroundColor={item.backgroundColor}
                />
              ))}
            </div>
          </div>
        )
      }
    </div>
  );
}

export default AdminDashboardContent;